package com.sddamico.draftmachine.ds

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

/**
 * Draft Machine — surface effects: radii, borders, shadows, glows, textures, gradients.
 * Full Kotlin translation of `tokens/effects.css`. Complex multi-layer shadows, SVG-data-URI
 * textures and multi-stop gradients are kept as CSS strings (there is no typed Kotlin form for
 * them) and applied through the `Modifier` helpers at the bottom of this file, so component code
 * stays declarative: `Modifier.shadowPlate()`, `.textureBrushed()`, `.gradientBrass()`.
 */
object DmEffects {
  // The blueprint grid behind every screen.
  const val gridBackdrop =
    "linear-gradient(rgba(90,110,124,.05) 1px,transparent 1px)," +
      "linear-gradient(90deg,rgba(90,110,124,.05) 1px,transparent 1px)"
  const val gridBackdropSize = "44px 44px"

  // ---- Corner radii (riveted plates: modest; gears: round) ----
  val radiusXs = 3.px
  val radiusSm = 5.px
  val radiusMd = 7.px // default card/plate
  val radiusLg = 12.px
  val radiusXl = 18.px
  val radiusPill = 999.px

  // ---- Border widths ----
  val borderHair = 1.px
  val borderPlate = 2.px // copper seam on emphasized panels

  // ---- Shadows: soft (Ghibli) + cool metal-plate inset highlight ----
  const val shadowFlat = "0 1px 0 rgba(0,0,0,.40)"
  const val shadowPlate =
    "inset 0 1px 0 rgba(226,238,247,.07), 0 2px 4px rgba(0,0,0,.48), 0 10px 26px rgba(0,0,0,.36)"
  const val shadowRaised =
    "inset 0 1px 0 rgba(226,238,247,.09), 0 4px 8px rgba(0,0,0,.48), 0 18px 44px rgba(0,0,0,.44)"
  const val shadowPop = "0 24px 70px rgba(0,0,0,.60), inset 0 1px 0 rgba(226,238,247,.11)"
  const val shadowInset = "inset 0 2px 6px rgba(0,0,0,.58)" // sunken slots/inputs

  // ---- Glows (lit metal & energized glass — use sparingly) ----
  const val glowBrass = "0 0 0 1px #9E5F30, 0 0 22px rgba(192,122,68,.34)" // copper
  const val glowGlass = "0 0 0 1px #22838A, 0 0 24px rgba(47,166,168,.42)" // aether energy
  const val glowRust = "0 0 0 1px #8E381F, 0 0 22px rgba(180,71,42,.32)"

  // ---- Focus ring (copper) ----
  const val ring = "0 0 0 3px rgba(192,122,68,.45)"

  // ---- Textures: cool brushed steel & cast speckle (SVG data URIs) ----
  // Brushed grain — organic streaked noise for broad metal surfaces
  const val textureBrushed =
    """url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='120'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.012 0.6' numOctaves='3' seed='7'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.16 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E")"""

  // Cast speckle — directionless fine grain; CTAs wear this for a tactile cast-metal feel
  const val textureCta =
    """url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='s'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='3'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.13 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23s)'/%3E%3C/svg%3E")"""

  // ---- Gradients ----
  const val gradientSteel = "linear-gradient(180deg, #1D242C 0%, #1D242C 100%)"
  const val gradientPlate = "linear-gradient(180deg, #EAEFF3 0%, #EAEFF3 100%)"
  const val gradientBrass = "linear-gradient(180deg, #D6935C 0%, #C07A44 52%, #9E5F30 100%)"
  const val gradientGlass = "linear-gradient(180deg, #4FC4C2 0%, #2FA6A8 55%, #22838A 100%)"
  const val gradientRust = "linear-gradient(180deg, #D0603A 0%, #B4472A 55%, #8E381F 100%)"
  // Active / "on the clock" wash — energized glass, not amber heat
  const val gradientHeat = "radial-gradient(120% 100% at 50% 0%, rgba(47,166,168,.16) 0%, rgba(0,0,0,0) 60%)"
  const val gradientEnergy = "radial-gradient(120% 100% at 50% 0%, rgba(47,166,168,.20) 0%, rgba(0,0,0,0) 62%)"
}

// ---- Modifier helpers: box-shadow ----

/** Apply an arbitrary CSS `box-shadow` value. */
fun Modifier.boxShadowRaw(value: String) = styleModifier { property("box-shadow", value) }

fun Modifier.shadowFlat() = boxShadowRaw(DmEffects.shadowFlat)
fun Modifier.shadowPlate() = boxShadowRaw(DmEffects.shadowPlate)
fun Modifier.shadowRaised() = boxShadowRaw(DmEffects.shadowRaised)
fun Modifier.shadowPop() = boxShadowRaw(DmEffects.shadowPop)
fun Modifier.shadowInset() = boxShadowRaw(DmEffects.shadowInset)

fun Modifier.glowBrass() = boxShadowRaw(DmEffects.glowBrass)
fun Modifier.glowGlass() = boxShadowRaw(DmEffects.glowGlass)
fun Modifier.glowRust() = boxShadowRaw(DmEffects.glowRust)

// ---- Modifier helpers: background image (textures & gradients) ----

/** Apply an arbitrary CSS `background-image` value (texture or gradient). */
fun Modifier.backgroundImageRaw(value: String) = styleModifier { property("background-image", value) }

fun Modifier.textureBrushed() = backgroundImageRaw(DmEffects.textureBrushed)
fun Modifier.textureCta() = backgroundImageRaw(DmEffects.textureCta)
fun Modifier.gradientSteel() = backgroundImageRaw(DmEffects.gradientSteel)
fun Modifier.gradientPlate() = backgroundImageRaw(DmEffects.gradientPlate)
fun Modifier.gradientBrass() = backgroundImageRaw(DmEffects.gradientBrass)
fun Modifier.gradientGlass() = backgroundImageRaw(DmEffects.gradientGlass)
fun Modifier.gradientRust() = backgroundImageRaw(DmEffects.gradientRust)

// ---- Composed surface helper ----

/**
 * A reusable brushed-steel panel surface — flat fill, hairline border, modest radius,
 * soft plate shadow. Mirrors the `.dm-panel` utility class from `tokens/base.css`.
 */
fun Modifier.dmPanel() = this
  .backgroundColor(DmColor.surfacePanel)
  .border(DmEffects.borderHair, LineStyle.Solid, DmColor.border)
  .borderRadius(DmEffects.radiusMd)
  .shadowPlate()

/**
 * The dark, faintly gridded backdrop behind the mobile app shell — flat [DmColor.bg] fill with
 * a 44px grid of hairline steel lines. Mirrors the `.dm-grid-backdrop` block duplicated across
 * the pitch/login screens.
 */
fun Modifier.dmGridBackdrop() = this
  .backgroundColor(DmColor.bg)
  .backgroundImageRaw(DmEffects.gridBackdrop)
  .styleModifier { property("background-size", DmEffects.gridBackdropSize) }
